// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, type ReactNode, type CSSProperties } from 'react'
import { ShieldCheck, Loader2, AlertCircle, RefreshCw, FileWarning } from 'lucide-react'
import * as api from '../../api/client'

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'

function gradeColor(score: number) {
  if (score >= 85) return '#15803d'
  if (score >= 75) return '#2563eb'
  if (score >= 65) return '#b45309'
  return '#b91c1c'
}
function statusColor(s: string): { fg: string; bg: string } {
  switch (s) {
    case 'pass': return { fg: '#15803d', bg: '#dcfce7' }
    case 'partial': return { fg: '#b45309', bg: '#fef3c7' }
    case 'fail': return { fg: '#b91c1c', bg: '#fee2e2' }
    case 'pending': return { fg: '#6b7280', bg: '#f3f4f6' }
    default: return { fg: '#6b7280', bg: '#f3f4f6' }
  }
}
function sevColor(s: string): { fg: string; bg: string } {
  switch (s) {
    case 'high': return { fg: '#b91c1c', bg: '#fee2e2' }
    case 'medium': return { fg: '#b45309', bg: '#fef3c7' }
    default: return { fg: '#374151', bg: '#f3f4f6' }
  }
}

function Pill({ text, c }: { text: string; c: { fg: string; bg: string } }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.02em' }}>{text}</span>
  )
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden', minWidth: 80 }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, borderRadius: 999 }} />
    </div>
  )
}

export default function SecurityDashboardTab() {
  const [data, setData] = useState<api.SecurityStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [rechecking, setRechecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void load(false) }, [])
  async function load(isRecheck: boolean) {
    isRecheck ? setRechecking(true) : setLoading(true)
    setError(null)
    try { setData(await api.getSecurityStatus()) }
    catch (e: any) { setError(e?.message || 'Failed to load security status') }
    finally { isRecheck ? setRechecking(false) : setLoading(false) }
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
            <ShieldCheck size={18} /> Security
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: MUTE }}>
            Live security posture across the platform's controls and gates. Read-only — this view runs
            configuration probes and never touches customer data.
          </p>
        </div>
        <button onClick={() => void load(true)} disabled={rechecking || loading} style={recheckBtn}>
          <RefreshCw size={14} className={rechecking ? 'spin' : undefined} /> {rechecking ? 'Rechecking…' : 'Recheck'}
        </button>
      </header>

      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTE }}><Loader2 size={18} className="spin" /> Loading…</div>
      ) : !data ? null : (
        <>
          {/* Two score axes */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card, flex: '1 1 240px' }}>
              <div style={cardLabel}>Technical posture</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 40, fontWeight: 700, color: gradeColor(data.technical.score), lineHeight: 1 }}>
                  {data.technical.score}
                </span>
                <span style={{ fontSize: 22, fontWeight: 700, color: gradeColor(data.technical.score) }}>{data.technical.grade}</span>
                <span style={{ fontSize: 12, color: MUTE }}>/ 100 · probed + evidenced</span>
              </div>
            </div>
            <div style={{ ...card, flex: '1 1 240px' }}>
              <div style={cardLabel}>Program attestation</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 40, fontWeight: 700, color: gradeColor(data.program.completeness_percent), lineHeight: 1 }}>
                  {data.program.completeness_percent}%
                </span>
                <span style={{ fontSize: 12, color: MUTE }}>{data.program.attested} / {data.program.total} attested</span>
              </div>
            </div>
          </div>

          {/* Gates */}
          <Section title="Security gates">
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
              {data.technical.gates.map((g, i) => (
                <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{g.name}</div>
                    <div style={{ fontSize: 12, color: MUTE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.drag}</div>
                  </div>
                  <div style={{ width: 44, textAlign: 'right', fontWeight: 700, color: gradeColor(g.score) }}>{g.score}</div>
                  <div style={{ flex: '0 0 120px' }}>
                    <Bar pct={g.percent_complete} color={gradeColor(g.score)} />
                    <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>{g.percent_complete}% complete · wt {Math.round(g.weight * 100)}%</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Controls */}
          <Section title="Controls in place">
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
              {data.controls.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
                  borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name} <span style={{ fontSize: 11, color: MUTE, fontWeight: 400 }}>· §{c.cite}</span></div>
                    <div style={{ fontSize: 12, color: MUTE }}>{c.evidence}</div>
                  </div>
                  <Pill text={c.status} c={statusColor(c.status)} />
                </div>
              ))}
            </div>
          </Section>

          {/* Open tasks */}
          <Section title={`Open items (${data.open_tasks.length})`}>
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
              {data.open_tasks.map((t, i) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                  borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                  <Pill text={t.severity} c={sevColor(t.severity)} />
                  <span style={{ fontSize: 12, color: MUTE, fontVariantNumeric: 'tabular-nums', width: 58 }}>{t.id}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{t.title}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Program controls (attestation required) */}
          <Section title="Program controls — attestation required">
            <p style={{ fontSize: 12, color: MUTE, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileWarning size={13} /> {data.program.note}
            </p>
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
              {data.program.controls.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                  borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                  <span style={{ fontSize: 12, color: MUTE, width: 52 }}>{p.id}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{p.name}</span>
                  <span style={{ fontSize: 12, color: MUTE }}>{p.owner}</span>
                  <Pill text={p.status} c={statusColor(p.status)} />
                </div>
              ))}
            </div>
          </Section>

          <p style={{ fontSize: 11, color: MUTE, marginTop: 16 }}>
            Generated {new Date(data.generated_at).toLocaleString()} · {data.source}
          </p>
        </>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h3 style={{ fontSize: 14, margin: '0 0 8px', fontWeight: 600 }}>{title}</h3>
      {children}
    </section>
  )
}

const card: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 8, padding: '14px 16px', background: 'var(--color-surface, #fff)' }
const cardLabel: CSSProperties = { fontSize: 12, color: MUTE, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.03em' }
const recheckBtn: CSSProperties = {
  flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
  border: `1px solid ${BORDER}`, color: 'var(--color-text, #111827)', padding: '7px 12px', fontSize: 13,
  borderRadius: 6, cursor: 'pointer', fontWeight: 500,
}
const errBox: CSSProperties = {
  background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px',
  borderRadius: 6, fontSize: 13, marginBottom: 12,
}
